import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AdminPageHeader,
  Theme,
  Modal,
  Button,
  ButtonTypes,
  ButtonSize,
  ButtonStyles,
  DropDown,
  MLPVersion,
  TableWithSearchTemplate,
} from '../../../ui/components';
import { LoggerService, dateTimeToCustomFormat } from '../../../ui/utils';
import { DropDownData } from '../../../ui/components/Form/DropDown/types/propsType';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import { useAPICall } from '../../../hooks';
import { DynamicObjectInterface } from '../../../sdk/type';
import InventoryService from '../../../sdk/services/inventoryService';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import CampaignService from '../../../sdk/services/campaignService';
import {
  MasterData,
  dataSearchOption,
  CampaignOption,
  getInventoryListConfig,
} from './inventoryConfig';

import styles from './index.module.scss';

interface AdminProps {
  adminType: number;
}

export const AdminGeneralInventoryList = ({ adminType }: AdminProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useAPICall();
  const [masterData, setMasterData] = useState<MasterData | null>();
  const [campaignList, setCampaignList] = useState<CampaignOption[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignOption>();
  const [displayEmpty, setDisplayEmpty] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [inventoryList, setInventoryList] = useState<DynamicObjectInterface[]>([]);
  const [defaultSearchOptions, setDefaultSearchOptions] = useState<DynamicObjectInterface>(null);
  const isUnauthorized = adminType !== null && ![1].includes(adminType)

  const { adminToast } = useAdminToast();

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (masterData) {
      setCampaignList(
        masterData.campaign
          ?.map(
            c =>
              ({
                ...c,
                title: `${c.campaignId} - ${c.campaignName}`,
                value: c.campaignId ?? '',
              }) as CampaignOption,
          )
          .sort((a, b) => {
            if (a.campaignStart > b.campaignStart) return -1;
            if (a.campaignStart < b.campaignStart) return 1;
            return 0;
          }),
      );
    }
  }, [masterData]);

  useEffect(() => {
    if (campaignList?.length) {
      const params = new URLSearchParams(location.search);
      const campaignId = params.get('campaignId') ?? '';
      if (campaignId) {
        setSelectedCampaign(prev => {
          let _selectedCampaign = campaignList.find(c => c.campaignId === campaignId);
          if (_selectedCampaign) return { ..._selectedCampaign };
          else return prev;
        });
      } else {
        let latest: CampaignOption = campaignList.reduce((latest: DynamicObjectInterface, current) => {
          const currentStartDate = new Date(current.campaignStart);
          const latestStartDate = latest ? new Date(latest.campaignStart) : null;

          if (!latestStartDate || currentStartDate > latestStartDate) return { ...current, value: current.campaignId };
          else return latest;
        }, null);
        navigate(`?campaignId=${latest.value}`, { replace: true });
      }
    }
  }, [campaignList, location]);

  useEffect(() => {
    if (selectedCampaign) {
      fetchInventoryListByCampaignId();
      let searchOptions = sessionStorage.getItem('FilterOptions');
      if (searchOptions) {
        setDefaultSearchOptions(JSON.parse(searchOptions));
      } else {
        setDefaultSearchOptions(null);
      }
    }
  }, [selectedCampaign]);

  const fetchInventoryListByCampaignId = async () => {
    let campaignId = selectedCampaign?.value || '';
    const result = await apiCall(InventoryService.getInventoryList, campaignId);
    if (result.status === 200) {
      setDisplayEmpty(result.data.data?.length === 0);
      const inventory = (result.data.data ?? []).map((item: any) => ({
        ...item,
        isUnauthorized: isUnauthorized,
        totalStock: (item.availableStock ?? 0) + (item.redeemedStock ?? 0),
        stockStatus: item.availableStock/(item.availableStock + item.redeemedStock) > 0.3 ? 'NORMAL' : 'LOW'
      }));

      setInventoryList(inventory);
    } else {
      setDisplayEmpty(true);
      LoggerService.log('Failed to retrieve participant list by campaign');
    }
  };

  const fetchMasterData = async () => {
    let list = {
      campaign: {
        endpoint: CampaignService.getCampaigns,
      },
    };
    try {
      const results = await Promise.all(Object.values(list)?.map(l => apiCall(l.endpoint)));

      setMasterData(
        Object.keys(list).reduce((result: DynamicObjectInterface, key, idx) => {
          result[key] = results[idx]?.data?.data?.filter(
            (campaign: DynamicObjectInterface) => campaign.campaignType === 'PROMOTION'
          );
          return result;
        }, {}),
      );
    } catch (error) {
      LoggerService.log('Failed to retrieve participant list', error);
    }
  };

  const onEdit = (id: string) => {
    if (id !== null) {
       navigate(`/admin/promotion/manage-inventory/edit?=${id}&campaignId=${selectedCampaign?.value}`)
    }
  };

    const onClick = (id: string) => {
    //to show pop up modal
    setSelectedRowId(id);
    setShowDelete(!isUnauthorized);
  };

  const handleInputChange = (value: DynamicObjectInterface) => {
    sessionStorage.removeItem('Pagination');
    navigate(`?campaignId=${value.campaignId}`);
  };

  const buttonConfig = [
    {
      title: t('admin.inventory.addInventory'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: isUnauthorized,
      onClick: () => {
        navigate(`/admin/promotion/manage-inventory/edit?=new&campaignId=${selectedCampaign?.value}`);
      },
    },
  ];

    const onDeleteAction = async () => {
    try {
      const response = await apiCall(InventoryService.deleteInventory, `${selectedRowId}`);

      if (response?.status === 200) {
        // Assuming the server responds with success status
        LoggerService.log('Inventory deleted successfully');
        adminToast({
          message: 'Inventory deleted successfully',
          theme: Theme.SUCCESS,
        });
        fetchInventoryListByCampaignId();
      } else {
        LoggerService.log('Failed to delete inventory');
      }
    } catch (error) {
      LoggerService.log('Error deleting inventory', error);
    } finally {
      // Close the modal whether the deletion was successful or not
      setShowDelete(false);
    }
  };

   const deletePrompt = (
    <>
      {showDelete && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.inventory.deleteInventory')}`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.inventory.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onDeleteAction();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.inventory.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.BLACK}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  return (
    <div>
      <AdminPageHeader title={t('admin.inventory.inventoryList')} button={buttonConfig}/>
      <div className={styles['coach-list-top']}>
        <DropDown
          name="campaignId"
          dropdownLabel={`${t('admin.teams.campaign')}`}
          disabledFilter={false}
          mlpVersion={MLPVersion.ONE}
          value={selectedCampaign?.value}
          data={campaignList as unknown as DropDownData[]}
          configData={{
            label: 'title',
            onSelect: (selectedOption: DropDownData) => {
              handleInputChange(selectedOption);
            },
          }}
          readOnly={false}
        />
        <div className={styles['coach-list-top-layout']}>
          <div className={styles['coach-list-top-info']}>
            <div>{t('admin.generalCoach.period')}:</div>
            <p>
              {selectedCampaign?.campaignStart && selectedCampaign.campaignEnd
                ? `${dateTimeToCustomFormat(selectedCampaign.campaignStart, 'DD MMM YYYY')} - ${dateTimeToCustomFormat(
                    selectedCampaign.campaignEnd,
                    'DD MMM YYYY',
                  )}`
                : '-'}
            </p>
          </div>
          <div className={styles['coach-list-top-info']}>
            <div>{t('admin.generalCoach.status')}:</div>
            <p>{selectedCampaign?.status ?? '-'}</p>
          </div>
        </div>
      </div>
      {displayEmpty ? (
        <div className={styles['no-results-container']}>
          <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
        </div>
      ) : (
        <TableWithSearchTemplate
          dataSource={inventoryList}
          searchByStrip
          getColumns={getInventoryListConfig}
          data={dataSearchOption}
          defaultSearchOptions={defaultSearchOptions}
          rowKey="recId"
          selected={`${selectedRowId}`}
          cb={{ onEdit, onClick }}
          onRowClick={row => {
          setSelectedRowId(row.recId);
        }}
        />
      )}
      {deletePrompt}
    </div>
  );
};
